import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useAction } from "convex/react";
import { openUrl } from "@tauri-apps/plugin-opener";
import { api } from "../../lib/convexApi";
import { buildAppManifest, convexSiteBase } from "../../lib/githubManifest";

function isTauri(): boolean {
  return Boolean((window as unknown as { __TAURI_INTERNALS__?: unknown }).__TAURI_INTERNALS__);
}

interface InstallRow {
  _id: string;
  installationId: number;
  accountLogin: string;
  installedBy: string;
  installedAt: number;
  // GitHub's installation account type. Optional because the githubInstall
  // table doesn't persist it yet — see manageInstallUrl() for why the default
  // is the org-scoped URL.
  accountType?: "Organization" | "User";
}

// GitHub exposes installation management at DIFFERENT URLs for orgs vs personal
// accounts; the personal form 404s for an org install and vice-versa. The old
// link hard-coded the personal form (`/settings/installations/<id>`), so every
// ORG install (the common case — Steerly binds each install to an orgId) opened
// an invalid page. Default to the org-scoped URL and only use the personal form
// when we positively know it's a User account.
function manageInstallUrl(install: InstallRow): string {
  if (install.accountType === "User") {
    return `https://github.com/settings/installations/${install.installationId}`;
  }
  return `https://github.com/organizations/${encodeURIComponent(
    install.accountLogin
  )}/settings/installations/${install.installationId}`;
}

// Parse "#github-integration?installed=987" → the query params. The `installed`
// flag is only a TRIGGER ("a callback just completed"); the authoritative
// install values are read from the server (pending-install), never the hash.
function parseHashParams(hash: string): URLSearchParams {
  const q = hash.indexOf("?");
  return new URLSearchParams(q === -1 ? "" : hash.slice(q + 1));
}

// Strip the install trigger from the hash but keep the tab anchor.
function clearInstallParams(): void {
  window.history.replaceState(null, "", `${window.location.pathname}#github-integration`);
}

export function GithubIntegrationTab() {
  const install = useQuery((api as any).github.queries.getInstall, {}) as
    | InstallRow
    | null
    | undefined;
  // Read the GitHub-App config STRAIGHT from Convex (not via the Bun host).
  // The host can only call Convex as the user when it has a Clerk-derived token
  // (CLERK_SECRET_KEY) or the forwarded X-Convex-Token — the bundled desktop app
  // has neither, so a host-brokered config probe silently returned "not
  // configured" even with credentials stored. The frontend already holds a
  // working Convex session (this is how the install row above loads), so reading
  // the config reactively here works on desktop + web for every user.
  // `canProvision` distinguishes the two audiences that land on this tab in the
  // not-configured state. The App is INSTANCE-wide (one per deployment, shared
  // by every org), so creating it is a deployment-operator act, not a customer
  // one — a customer admin who created it would own the private key the whole
  // deployment runs on. On hosted this is false and the customer is told to wait
  // for the operator; on self-host the operator IS the admin, so it is true.
  const cfg = useQuery((api as any).githubApp.queries.config, {}) as
    | {
        configured: boolean;
        slug: string | null;
        canProvision: boolean;
        visibility: "public" | "private";
      }
    | undefined;
  // The last setup failure, if any. `convertAndStore` runs scheduled, so its
  // throws (e.g. a missing VAULT_MASTER_KEY) are otherwise invisible — surface
  // it here so "not configured" comes with an actionable reason.
  const setupError = useQuery((api as any).githubApp.queries.setupError, {}) as
    | { at: number; message: string }
    | null
    | undefined;
  const issueManifestState = useMutation((api as any).githubApp.mutations.issueManifestState);
  const issueInstallState = useMutation((api as any).github.mutations.issueInstallState);
  const reconcileInstall = useAction((api as any).githubApp.actions.reconcileInstall);
  // NOTE: githubApp.actions.connectInstall is deliberately NOT bound here. It
  // refuses every call this tab could make (hosted → "use_install_flow"), and
  // its one live use is the self-host escape hatch for a deployment with
  // several installations of its own App — which has no UI.

  const [installLoading, setInstallLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appCreated] = useState(() =>
    typeof window !== "undefined" ? window.location.hash.includes("app_created=1") : false
  );
  // Strip the app_created flag from the URL once the note has read it, so a
  // reload doesn't re-show "finishing setup…".
  useEffect(() => {
    if (appCreated) clearInstallParams();
  }, [appCreated]);
  // When handleInstall learns the host can't build an install URL (App not
  // provisioned host-side), that verdict overrides the Convex config status.
  const [installUnprovisioned, setInstallUnprovisioned] = useState(false);
  // Guards the connect-completion effect against a double fire (e.g. React
  // StrictMode's mount/unmount/mount), since the hash is now cleared
  // asynchronously (after the write) rather than synchronously.
  const connectStartedRef = useRef(false);
  // Auto-detect ("reconcile") state: on the desktop the post-install redirect
  // can't record the install, so when the App is configured but no install is
  // recorded we ask GitHub directly which accounts it's installed on.
  const [reconciling, setReconciling] = useState(false);
  const [reconcileHint, setReconcileHint] = useState<string | null>(null);
  const reconcileStartedRef = useRef(false);
  // A detected-but-unbound installation (hosted multi-tenant): reconcile never
  // auto-binds a single shared-App installation. Holding it lets us name the
  // account in the empty state and label the install button with it — the bind
  // itself still has to come back through GitHub's state-verified redirect.
  const [candidate, setCandidate] = useState<{
    installationId: number;
    accountLogin: string;
  } | null>(null);

  // configured: undefined = Convex config still loading; else known.
  const configured = installUnprovisioned ? false : cfg?.configured;

  // Detect + connect an existing installation. Runs once automatically when the
  // App is configured but no install is recorded (the desktop redirect handshake
  // can't do it), and again on demand via the "Detect installation" button.
  async function runReconcile() {
    setReconciling(true);
    setReconcileHint(null);
    setCandidate(null);
    setError(null);
    try {
      const r = (await reconcileInstall({})) as {
        status: string;
        count?: number;
        installationId?: number;
        accountLogin?: string;
      };
      // "connected" / "already" → the install useQuery flips reactively.
      if (r.status === "candidate" && r.installationId) {
        // Hosted: the App IS installed on this account, but seeing it in the
        // shared App's installation list does not prove THIS org controls it
        // (the App is instance-wide, so the list spans every tenant). The bind
        // therefore has to come from GitHub's own state-verified install
        // redirect — see the candidate button below, which starts exactly that.
        setCandidate({ installationId: r.installationId, accountLogin: r.accountLogin ?? "" });
      } else if (r.status === "multiple") {
        setReconcileHint(
          `Found ${r.count} installations of this App — use “Install on GitHub” to connect this organization's.`
        );
      } else if (r.status === "none") {
        setReconcileHint(
          "No installation found yet. Click “Install on GitHub”, then “Detect installation”."
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to detect installation");
    } finally {
      setReconciling(false);
    }
  }

  useEffect(() => {
    if (cfg?.configured !== true) return; // not configured (or still loading)
    if (install !== null) return; // already have one (or install still loading)
    if (reconcileStartedRef.current) return; // once per mount
    reconcileStartedRef.current = true;
    void runReconcile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.configured, install]);

  // After GitHub redirects back, the hash carries only a TRIGGER (`installed`).
  // The Convex install row is bound SERVER-SIDE by the host's state-verified
  // install-callback (the trust anchor), so there is nothing for the SPA to
  // write — `getInstall` flips to connected reactively. We only strip the
  // trigger from the URL so a reload doesn't re-show it. A forged
  // `?installed=<id>` link therefore binds nothing client-side (the value is
  // never trusted; only the host's state-verified pair was written to Convex).
  useEffect(() => {
    // connectStartedRef (a ref, so it survives StrictMode's dev mount/unmount/
    // mount) dedupes the trigger handling across the double-invoke.
    if (connectStartedRef.current) return;
    const params = parseHashParams(window.location.hash);
    if (!params.get("installed")) return; // not a fresh callback
    connectStartedRef.current = true;
    clearInstallParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCreateApp() {
    setCreating(true);
    setError(null);
    try {
      const returnUrl = window.location.href.split("#")[0];
      const { token } = await issueManifestState({ returnUrl });
      const convexUrl = import.meta.env.VITE_CONVEX_URL as string;
      const siteBase = convexSiteBase(convexUrl);
      const manifest = buildAppManifest({
        siteBase,
        // Public product homepage (a GitHub App's homepage must be a stable
        // public URL — not the desktop's localhost origin). Overridable for
        // self-host via VITE_PUBLIC_WEB_URL.
        homepageUrl: (import.meta.env.VITE_PUBLIC_WEB_URL as string) || "https://steerly.ai",
        // Clean default name (GitHub App names are globally unique — the user
        // can adjust on GitHub's create screen if it's taken).
        name: "Steerly Workbench",
        // Hosted → public, so customer orgs can install the shared App.
        // Self-host → private, since only the operator's own account ever
        // installs it. The backend decides; the client must not guess.
        visibility: cfg?.visibility ?? "private",
      });
      // GitHub's manifest flow needs a real POST, and the Tauri WebView can't
      // open a scriptable popup (window.open returns null) — that's why the
      // workbench previously navigated itself to github.com and 404'd. Instead we
      // open a tiny host-served bridge page in the user's SYSTEM browser (where
      // they're signed into GitHub) via the Tauri opener; the bridge auto-submits
      // the manifest POST to github.com. state + manifest ride in the URL hash, so
      // they never hit our own server.
      const bridge =
        `${window.location.origin}/github-app-create.html` +
        `#state=${encodeURIComponent(token)}` +
        `&manifest=${encodeURIComponent(JSON.stringify(manifest))}`;
      if (isTauri()) {
        await openUrl(bridge);
      } else {
        const w = window.open(bridge, "_blank", "noopener,noreferrer");
        if (!w) {
          setError("Could not open a window — please allow popups for this site, then try again.");
          return;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start GitHub App creation");
    } finally {
      setCreating(false);
    }
  }

  async function handleInstall() {
    setInstallLoading(true);
    setError(null);
    try {
      // Mint the CSRF state in CONVEX and build the install URL here, instead of
      // asking the host for it.
      //
      // The host used to mint the state in an in-memory Map and the callback
      // authorized its Convex write with AGENTGUARD_HOST_AUDIT_SECRET. The
      // desktop app deliberately never ships that secret, so the write 403'd and
      // no desktop install ever produced a githubInstall row. A Convex-minted
      // token lets the public callback prove the org without holding a secret.
      //
      // Two things fall out of doing it here: the tab already holds an
      // authenticated Convex session (the host cannot authenticate as the user
      // at all), and starting an install no longer needs a local host — so this
      // works on the hosted web app too, where /api/* has nothing behind it.
      const slug = cfg?.slug;
      if (!cfg?.configured || !slug) {
        setInstallUnprovisioned(true); // overrides the Convex config status
        return;
      }
      // The origin to come back to rides on the state token, not on the App.
      // GitHub's post-install redirect goes to the App's frozen setup_url —
      // now the shared convex.site callback — which reads this to 302 the
      // browser home. Strip the hash: it is re-added by the callback, and the
      // current one is `#github-integration`, which would double up.
      const returnUrl = window.location.href.split("#")[0];
      const { state } = (await issueInstallState({ returnUrl })) as { state: string };
      const url = `https://github.com/apps/${encodeURIComponent(
        slug
      )}/installations/new?state=${encodeURIComponent(state)}`;
      // GitHub's install page must open in the user's SYSTEM browser (where
      // they're signed into GitHub). In the Tauri WebView window.open returns
      // null, so use the Tauri opener — mirroring the Create-App flow (#175).
      if (isTauri()) {
        await openUrl(url);
      } else {
        const w = window.open(url, "_blank", "noopener,noreferrer");
        if (!w) {
          setError("Could not open a window — please allow popups for this site, then try again.");
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start the GitHub install");
    } finally {
      setInstallLoading(false);
    }
  }

  const loading = install === undefined || cfg === undefined;

  return (
    <div className="ac-github-tab">
      {appCreated && (
        <div className="ac-github-tab__note" role="status">
          GitHub App created — finishing setup…
        </div>
      )}
      <div className="ac-github-tab__header">
        <div>
          <h3 className="ac-github-tab__title">GitHub</h3>
          <p className="ac-github-tab__subtitle">
            Connect your GitHub organization to feed pull request events into the Steerly risk
            pipeline.
          </p>
        </div>
        {!loading && configured !== false && (
          <button
            className="ac-github-install-cta"
            onClick={handleInstall}
            disabled={installLoading}
            aria-label="Install on GitHub"
          >
            {installLoading
              ? "Opening…"
              : install
                ? "Reinstall on GitHub →"
                : "Install on GitHub →"}
          </button>
        )}
      </div>

      {error && (
        <div className="ac-github-tab__error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="ac-github-tab__loading" aria-live="polite">
          Loading…
        </div>
      ) : configured === false ? (
        <div className="ac-github-tab__not-configured">
          <strong>GitHub App not configured</strong>
          {setupError && cfg?.canProvision && (
            <div className="ac-github-tab__error" role="alert">
              The last GitHub App setup attempt failed: {setupError.message}. Fix the issue and try
              again.
            </div>
          )}
          {cfg?.canProvision ? (
            <>
              <button
                className="ac-github-install-cta"
                onClick={handleCreateApp}
                disabled={creating}
                aria-label="Create GitHub App"
              >
                {creating ? "Opening…" : "Create GitHub App →"}
              </button>
              <p>
                This creates the GitHub App for <strong>this whole deployment</strong> — every
                organization on it installs the same App, under whichever GitHub account you create
                it with. See <code>docs/onboarding/github-app-provisioning.md</code>.
              </p>
              {/* The manual alternative to the button above. Still supported:
                  the webhook secret resolves store-first with an env fallback,
                  and the Bun host reads the App id/key from its own env. Kept
                  in the operator branch — a customer admin has no deployment
                  env to set these in. */}
              <p>Or provision it by hand: create the App on GitHub and set these, then reload.</p>
              <ul className="ac-github-env-list">
                <li>
                  <code>GITHUB_APP_NAME</code>, <code>GITHUB_APP_ID</code>,{" "}
                  <code>GITHUB_APP_PRIVATE_KEY</code> — on the Bun host
                </li>
                <li>
                  <code>GITHUB_WEBHOOK_SECRET</code> — on Convex
                </li>
              </ul>
            </>
          ) : (
            // Hosted customer admin. There is nothing for them to do here: the
            // App is provisioned once by whoever runs the deployment. Showing a
            // create button (as this tab used to, for everyone) invited them to
            // create an App the rest of the deployment would then depend on.
            <p>
              This deployment&rsquo;s administrator hasn&rsquo;t connected the GitHub App yet. Once
              they do, &lsquo;Install on GitHub&rsquo; appears here and you choose which
              repositories to grant access to.
            </p>
          )}
        </div>
      ) : !install ? (
        <div className="ac-github-tab__empty">
          {reconciling ? (
            <span aria-live="polite">Checking for an existing installation…</span>
          ) : candidate ? (
            <span>
              Found an installation on <strong>{candidate.accountLogin}</strong>. Finish connecting
              it on GitHub to link it to this organization.
            </span>
          ) : reconcileHint ? (
            <span>{reconcileHint}</span>
          ) : (
            <span>
              No GitHub installation yet. Click &lsquo;Install on GitHub&rsquo; to connect your
              organization.
            </span>
          )}
          <div className="ac-github-installation__actions">
            {candidate ? (
              // Starts the SAME state-verified install redirect as the header
              // CTA — not a bind from the detected id.
              //
              // This button used to call connectInstall(candidate.installationId),
              // which could never succeed: reconcileInstall only yields a
              // "candidate" when AGENTGUARD_SELF_HOST !== "1", and connectInstall
              // refuses to bind for exactly that case (returns "use_install_flow",
              // convex/githubApp/actions.ts:236). Clicking it therefore just
              // swapped itself for a hint telling you to press the other button —
              // which reads as a dead click, and is what users reported.
              <button
                className="ac-github-installation__manage-link"
                onClick={handleInstall}
                disabled={installLoading}
                // Matches the visible label (WCAG 2.5.3 Label in Name) so voice
                // control can activate it by what the user can actually read.
                aria-label={`Connect ${candidate.accountLogin} on GitHub`}
              >
                {installLoading ? "Opening…" : `Connect ${candidate.accountLogin} on GitHub →`}
              </button>
            ) : (
              <button
                className="ac-github-installation__manage-link"
                onClick={runReconcile}
                disabled={reconciling}
                aria-label="Detect installation"
              >
                {reconciling ? "Detecting…" : "Detect installation"}
              </button>
            )}
          </div>
        </div>
      ) : (
        <ul className="ac-github-installations" role="list">
          <li key={install._id} className="ac-github-installation">
            <div className="ac-github-installation__account">
              <span className="ac-github-installation__login">{install.accountLogin}</span>
            </div>
            <div className="ac-github-installation__meta">
              Installed {new Date(install.installedAt).toLocaleDateString()} · installation #
              {install.installationId}
            </div>
            <div className="ac-github-installation__actions">
              <a
                href={manageInstallUrl(install)}
                target="_blank"
                rel="noopener noreferrer"
                className="ac-github-installation__manage-link"
              >
                Manage on GitHub
              </a>
            </div>
          </li>
        </ul>
      )}
    </div>
  );
}
